// src/frontend/js/modern/core/state-cache.js
import { normalizeItems } from "../shared/store-helpers";
import conflictManager from "./conflict-manager";

/**
 * StateCache — Single source of truth at runtime.
 *
 * Architecture:
 *  - All data kept parsed in RAM
 *  - Synchronous reads (no JSON.parse, no await)
 *  - Async batched writes to persistence layer
 *  - Single path for all updates (no race conditions)
 *  - Differential updates support
 *  - Reactive subscriptions with microtask batching:
 *    multiple mutations within the same tick produce exactly ONE
 *    notification per module, so UI renders once per user gesture.
 *
 * Optimistic items (_optimistic: true) are treated as truth until the
 * server proves otherwise. They are always counted and rendered.
 */
class StateCache {
	constructor() {
		this._data = {
			wishlist: { items: [], count: 0 },
			waitlist: { items: [], count: 0 },
			multiList: { lists: [], count: 0 },
			config: { userId: '', sessionId: '', isLoggedIn: false, mergeLists: false },
			ui: { isProcessing: false, pendingActions: 0, lastError: null, isOnline: navigator.onLine, activeTab: 'wishlist' },
		};
		this._index = {
			wishlist: new Map(),
			waitlist: new Map(),
			multiList: new Map(),
		};
		this._listeners = new Map();
		this._version = 0;
		this._flushTimer = null;
		this._dirty = new Set();
		this._pendingNotifyModules = new Set();
		this._notifyScheduled = false;
	}
	getList(listId) {
		return this._index.multiList.get(String(listId)) || null;
	}
	setConfig(config) {
		this._data.config = { ...this._data.config, ...config };
		if ('mergeLists' in this._data.config) {
			this._data.config.mergeLists =
				this._data.config.mergeLists === true ||
				this._data.config.mergeLists === 'true';
		}
		this._markDirty('config');
	}
	getConfig() {
		return this._data.config;
	}
	getUIState() {
		return this._data.ui;
	}
	setUIState(state) {
		this._data.ui = { ...this._data.ui, ...state };
		this._markDirty('ui');
	}
	getItems(module) {
		if (module === 'wishlist') {
			return this._getWishlistItemsWithMergeLogic();
		}
		return this._data[module]?.items || [];
	}
	getCount(module) {
		if (module === 'wishlist') {
			return this._getWishlistItemsWithMergeLogic().length;
		}
		return this._data[module]?.count || 0;
	}
	getLists() {
		return this._data.multiList.lists || [];
	}
	getItem(module, id) {
		const key = String(id);
		const index = this._index[module];
		if (!index) return null;
		return index.get(key) || null;
	}
	hasItem(module, id) {
		const key = String(id);
		const index = this._index[module];
		return index ? index.has(key) : false;
	}
	getProductListCount(productId) {
		const pid = String(productId);
		let count = 0;
		for (const list of this._data.multiList.lists) {
			const items = list.wishlist_items || [];
			if (items.some(item => String(item.product_id) === pid)) {
				count++;
			}
		}
		return count;
	}
	getFullState() {
		return {
			wishlist: { items: this._data.wishlist.items, count: this._data.wishlist.count },
			waitlist: { items: this._data.waitlist.items, count: this._data.waitlist.count },
			multiList: { lists: this._data.multiList.lists, count: this._data.multiList.count },
			config: { ...this._data.config },
			ui: { ...this._data.ui },
		};
	}

	/**
	 * Public accessor for the merged/deduplicated wishlist item set.
	 * Used by BaseListUI.syncCounterItems and WishlistUI.syncAddButtons
	 * so the counter always reflects the true merged state.
	 */
	getMergedWishlistItems() {
		return this._getWishlistItemsWithMergeLogic();
	}

	/**
	 * Check whether a product exists in ANY multi-list OR in the raw
	 * wishlist array (covers optimistic adds not yet assigned to a list).
	 * In merge mode this determines the "exists" state of add-to-wishlist
	 * buttons regardless of which specific list holds the product.
	 */
	isProductInAnyList(productId) {
		const pid = String(productId);
		for (const list of this._data.multiList.lists) {
			const items = list.wishlist_items || [];
			if (items.some(item => String(item.product_id) === pid)) {
				return true;
			}
		}
		const rawItems = this._data.wishlist?.items || [];
		return rawItems.some(item => String(item.product_id) === pid);
	}

	/**
	 * Merge Lists logic — replicates the old wishlistItems computed behavior.
	 *
	 * Non-merge mode: only items belonging to the default wishlist are shown.
	 * Merge mode: collect items from BOTH the flat wishlist array AND every
	 *             multi-list's wishlist_items, deduplicate by product_id
	 *             (keep highest item_id), attach concatenated list_name,
	 *             sort by item_id desc.
	 *
	 * Optimistic items (_optimistic: true) are ALWAYS included in both modes.
	 */
	_getWishlistItemsWithMergeLogic() {
		const rawItems = this._data.wishlist?.items || [];
		const isMerge = this._data.config?.mergeLists === true;

		// Build lookup maps from multiList data
		const lists = this._data.multiList?.lists || [];
		let defaultWishlistId = null;
		const listNameMap = new Map();
		const productListNamesMap = new Map();
		for (const list of lists) {
			const listId = String(list.wishlist_id ?? list.id ?? '');
			const listName = list.wishlist_name ?? list.name ?? '';
			if (list.is_default === true || list.is_default === 'true' || list.is_default === 1 || list.is_default === '1') {
				defaultWishlistId = listId;
			}
			if (listId) {
				listNameMap.set(listId, listName);
			}
			const listItems = normalizeItems(list.wishlist_items || list.items);
			for (const item of listItems) {
				if (!item || item.product_id === undefined) continue;
				const pid = String(item.product_id);
				if (!productListNamesMap.has(pid)) {
					productListNamesMap.set(pid, new Set());
				}
				productListNamesMap.get(pid).add(listName);
			}
		}

		// In merge mode, collect items from multi-list wishlist_items that
		// may not exist in the flat wishlist array yet. This ensures the
		// counter reflects ALL products across ALL lists immediately.
		let mergedSourceItems = [...rawItems];
		if (isMerge) {
			const existingPids = new Set(rawItems.map(i => String(i.product_id)));
			for (const list of lists) {
				const listItems = normalizeItems(list.wishlist_items || list.items);
				for (const item of listItems) {
					if (!item || item.product_id === undefined) continue;
					const pid = String(item.product_id);
					if (!existingPids.has(pid)) {
						mergedSourceItems.push({
							...item,
							wishlist_id: list.wishlist_id ?? list.id ?? '',
						});
						existingPids.add(pid);
					}
				}
			}
		}

		if (!mergedSourceItems.length) return [];

		// Non-merge mode: filter to default list only.
		// Optimistic items bypass the filter (no confirmed wishlist_id yet).
		if (!isMerge) {
			if (defaultWishlistId !== null) {
				return mergedSourceItems.filter(item =>
					item._optimistic === true ||
					String(item.wishlist_id) === String(defaultWishlistId)
				);
			}
			return mergedSourceItems;
		}

		// Merge mode: keep items whose wishlist_id belongs to a known list,
		// plus all optimistic items.
		const validListIds = new Set(listNameMap.keys());
		const filtered = mergedSourceItems.filter(item =>
			item._optimistic === true ||
			validListIds.has(String(item.wishlist_id)) ||
			lists.some(l => String(l.wishlist_id ?? l.id) === String(item.wishlist_id))
		);

		// Deduplicate: keep the item with the highest item_id per product
		const byProduct = new Map();
		for (const item of filtered) {
			if (!item || item.product_id === undefined) continue;
			const pid = String(item.product_id);
			const existing = byProduct.get(pid);
			if (!existing || parseInt(item.item_id || 0, 10) > parseInt(existing.item_id || 0, 10)) {
				byProduct.set(pid, item);
			}
		}

		// Build result with concatenated list_name
		const result = [];
		for (const [, item] of byProduct) {
			const pid = String(item.product_id);
			const listNames = productListNamesMap.get(pid);
			const mergedListName = listNames ? [...listNames].join(', ') : '';
			result.push({ ...item, list_name: mergedListName });
		}

		// Sort by item_id descending (newest first)
		result.sort((a, b) =>
			parseInt(b.item_id || 0, 10) - parseInt(a.item_id || 0, 10)
		);
		return result;
	}

	setModuleData(module, data, count) {
		let finalItems = data || [];
		try {
			const recentOps = conflictManager?.getOperations?.() || [];
			const pendingRemove = new Set();
			const pendingAdd = new Set();
			const RECENT_CONFIRM_WINDOW_MS = 10000;
			const now = Date.now();
			const multiListPendingRemove = new Map();
			const multiListPendingAdd = new Map();
			for (const group of recentOps) {
				for (const op of group.operations) {
					if (op.module === module) {
						const isPending = op.state === 'queued' || op.state === 'in_flight';
						const confirmationTime = op.confirmedAt || op.timestamp;
						const isRecentlyConfirmed = op.state === 'confirmed' && (now - confirmationTime) < RECENT_CONFIRM_WINDOW_MS;
						if (!isPending && !isRecentlyConfirmed) continue;
						if (module === 'multiList') {
							if (['create', 'delete', 'update', 'privacy'].includes(op.type) || (op.type === 'add' && !op.productId)) {
								const listId = String(op.listId);
								if (!listId || listId === 'undefined') continue;
								if (op.type === 'delete') {
									pendingRemove.add(listId);
									pendingAdd.delete(listId);
								} else if (op.type === 'create') {
									if (!pendingRemove.has(listId)) pendingAdd.add(listId);
								}
							} else if (op.productId) {
								const productId = String(op.productId);
								const listId = op.listId ? String(op.listId) : '__ALL__';
								if (op.type === 'remove' || op.type === 'delete') {
									if (!multiListPendingRemove.has(listId)) multiListPendingRemove.set(listId, new Set());
									multiListPendingRemove.get(listId).add(productId);
								} else if (op.type === 'add') {
									if (!multiListPendingAdd.has(listId)) multiListPendingAdd.set(listId, new Set());
									multiListPendingAdd.get(listId).add(productId);
								}
							}
						} else {
							let id = String(op.productId || op.cartItemKey || op.itemId);
							if (!id || id === 'undefined') continue;
							if (op.type === 'remove' || op.type === 'delete') {
								pendingRemove.add(id);
								pendingAdd.delete(id);
							} else if (op.type === 'add' || op.type === 'create') {
								if (!pendingRemove.has(id)) pendingAdd.add(id);
							}
						}
					}
				}
			}
			const currentItems = this._data[module]?.items || this._data[module]?.lists || [];
			const keyField = module === 'multiList' ? 'wishlist_id' : 'product_id';
			if (module === 'multiList') {
				if (pendingRemove.size > 0) {
					finalItems = finalItems.filter(list => !pendingRemove.has(String(list.wishlist_id || list.id)));
				}
				if (pendingAdd.size > 0) {
					const serverListIds = new Set(finalItems.map(l => String(l.wishlist_id || l.id)));
					for (const listId of pendingAdd) {
						if (!serverListIds.has(listId)) {
							const localList = currentItems.find(l => String(l.wishlist_id || l.id) === listId);
							if (localList) finalItems.push(localList);
						}
					}
				}
				if (multiListPendingRemove.size > 0 || multiListPendingAdd.size > 0) {
					finalItems = finalItems.map(list => {
						const listId = String(list.wishlist_id || list.id);
						let items = Array.isArray(list.wishlist_items) ? [...list.wishlist_items] : [];
						let modified = false;
						const pRemove = multiListPendingRemove.get(listId);
						const pRemoveAll = multiListPendingRemove.get('__ALL__');
						if ((pRemove && pRemove.size > 0) || (pRemoveAll && pRemoveAll.size > 0)) {
							const before = items.length;
							items = items.filter(i => {
								const pid = String(i.product_id);
								return !(pRemove?.has(pid) || pRemoveAll?.has(pid));
							});
							if (items.length !== before) modified = true;
						}
						const pAdd = multiListPendingAdd.get(listId);
						const pAddAll = multiListPendingAdd.get('__ALL__');
						if ((pAdd && pAdd.size > 0) || (pAddAll && pAddAll.size > 0)) {
							const serverPids = new Set(items.map(i => String(i.product_id)));
							const localList = currentItems.find(l => String(l.wishlist_id || l.id) === listId);
							const localItems = localList?.wishlist_items || [];
							const pidsToCheck = new Set([...(pAdd || []), ...(pAddAll || [])]);
							for (const pid of pidsToCheck) {
								if (!serverPids.has(pid)) {
									const localItem = localItems.find(i => String(i.product_id) === pid);
									if (localItem) {
										items.push(localItem);
										modified = true;
									}
								}
							}
						}
						if (modified) return { ...list, wishlist_items: items };
						return list;
					});
				}
			} else {
				if (pendingRemove.size > 0 || pendingAdd.size > 0) {
					if (pendingRemove.size > 0) {
						finalItems = finalItems.filter(item => {
							const pid = String(item[keyField] || item.product_id || item.wishlist_id);
							return !pendingRemove.has(pid);
						});
					}
					if (pendingAdd.size > 0) {
						const serverPids = new Set(finalItems.map(i => String(i[keyField] || i.product_id || i.wishlist_id)));
						for (const pid of pendingAdd) {
							if (!serverPids.has(pid)) {
								const localItem = currentItems.find(i => String(i[keyField] || i.product_id || i.wishlist_id) === pid);
								if (localItem) finalItems.push(localItem);
							}
						}
					}
				}
			}
		} catch (e) {
			console.warn('[StateCache] Merge with recent operations failed:', e);
		}
		if (module === 'multiList') {
			this._data[module] = { lists: finalItems, count: count ?? (finalItems ? finalItems.length : 0) };
		} else {
			this._data[module] = { items: finalItems, count: count ?? (finalItems ? finalItems.length : 0) };
		}
		this._rebuildIndex(module);
		this._version++;
		this._markDirty(module);
		this._notify(module, 'replace');
	}
	upsertItem(module, id, itemData) {
		const key = String(id);
		const index = this._index[module];
		if (!index) return;
		const existing = index.get(key);
		if (existing) {
			Object.assign(existing, itemData);
		} else {
			const newItem = { ...itemData };
			this._data[module].items.push(newItem);
			index.set(key, newItem);
			this._data[module].count++;
		}
		this._version++;
		this._markDirty(module);
		this._notify(module, 'upsert', { id });
	}
	removeItem(module, id) {
		const key = String(id);
		const index = this._index[module];
		if (!index || !index.has(key)) return false;
		index.delete(key);
		const items = this._data[module].items;
		const idx = items.findIndex(item => String(item['product_id']) === key);
		if (idx !== -1) {
			items.splice(idx, 1);
			this._data[module].count--;
		}
		this._version++;
		this._markDirty(module);
		this._notify(module, 'remove', { id });
		return true;
	}
	updateItem(module, id, updates) {
		const key = String(id);
		const index = this._index[module];
		if (!index) return false;
		const item = index.get(key);
		if (!item) return false;
		Object.assign(item, updates);
		this._version++;
		this._markDirty(module);
		this._notify(module, 'update', { id });
		return true;
	}
	confirmItem(module, id, serverData) {
		const key = String(id);
		const index = this._index[module];
		if (!index) return;
		const item = index.get(key);
		if (!item) return;
		const { display, _optimistic, _timestamp, ...cleanData } = serverData || {};
		Object.assign(item, cleanData, { _optimistic: false });
		this._version++;
		this._markDirty(module);
		this._notify(module, 'confirm', { id });
	}
	rollbackItem(module, id) {
		const key = String(id);
		const index = this._index[module];
		if (!index) return;
		const item = index.get(key);
		if (!item || item._optimistic !== true) return;
		this.removeItem(module, id);
	}
	addList(listData) {
		const lists = this._data.multiList.lists;
		const newList = { ...listData, _optimistic: true, _timestamp: Date.now() };
		lists.push(newList);
		this._data.multiList.count = lists.length;
		this._index.multiList.set(String(listData.wishlist_id), newList);
		this._version++;
		this._markDirty('multiList');
		this._notify('multiList', 'list-added', { listId: listData.wishlist_id });
		// In merge mode, list changes affect the wishlist counter too
		if (this._data.config?.mergeLists) {
			this._notify('wishlist', 'multi-list-changed');
		}
		return newList;
	}
	updateList(listId, updates) {
		const list = this._index.multiList.get(String(listId));
		if (!list) return false;
		const oldWishlistId = String(list.wishlist_id);
		Object.assign(list, updates);
		const newWishlistId = String(list.wishlist_id);
		if (oldWishlistId !== newWishlistId) {
			this._index.multiList.delete(oldWishlistId);
			this._index.multiList.set(newWishlistId, list);
		}
		this._version++;
		this._markDirty('multiList');
		this._notify('multiList', 'list-updated', { listId: newWishlistId });
		// In merge mode, item-level changes inside a list affect the wishlist counter
		if (this._data.config?.mergeLists && ('wishlist_items' in updates)) {
			this._notify('wishlist', 'multi-list-changed');
		}
		return true;
	}
	removeList(listId) {
		const key = String(listId);
		const lists = this._data.multiList.lists;
		const idx = lists.findIndex(l => String(l.wishlist_id) === key);
		if (idx === -1) return false;
		lists.splice(idx, 1);
		this._index.multiList.delete(key);
		this._data.multiList.count = lists.length;
		this._version++;
		this._markDirty('multiList');
		this._notify('multiList', 'list-removed', { listId });
		// In merge mode, removing a list removes its products from the merged view
		if (this._data.config?.mergeLists) {
			this._notify('wishlist', 'multi-list-changed');
		}
		return true;
	}
	addItemToList(listId, productId, itemData = {}) {
		const list = this._index.multiList.get(String(listId));
		if (!list) return false;
		if (!list.wishlist_items) list.wishlist_items = [];
		const exists = list.wishlist_items.some(item => String(item.product_id) === String(productId));
		if (exists) return false;
		const newItem = {
			product_id: productId,
			item_id: itemData.item_id || null,
			quantity: itemData.quantity || 1,
			_optimistic: true,
			_timestamp: Date.now(),
			...itemData,
		};
		list.wishlist_items.push(newItem);
		this._version++;
		this._markDirty('multiList');
		this._notify('multiList', 'item-added', { listId, productId });
		// In merge mode, adding an item to any list must trigger the
		// wishlist counter re-render because the merged view includes all lists.
		if (this._data.config?.mergeLists) {
			this._notify('wishlist', 'multi-list-changed');
		}
		return true;
	}
	removeItemFromList(listId, productId) {
		const list = this._index.multiList.get(String(listId));
		if (!list || !list.wishlist_items) return false;
		const before = list.wishlist_items.length;
		list.wishlist_items = list.wishlist_items.filter(
			item => String(item.product_id) !== String(productId)
		);
		if (list.wishlist_items.length === before) return false;
		this._version++;
		this._markDirty('multiList');
		this._notify('multiList', 'item-removed', { listId, productId });
		if (this._data.config?.mergeLists) {
			const pid = String(productId);
			const stillInAnotherList = this._data.multiList.lists.some(l => {
				if (String(l.wishlist_id) === String(listId)) return false;
				return (l.wishlist_items || []).some(i => String(i.product_id) === pid);
			});
			if (!stillInAnotherList) {
				const rawItems = this._data.wishlist.items;
				const rawBefore = rawItems.length;
				this._data.wishlist.items = rawItems.filter(
					item => String(item.product_id) !== pid
				);
				if (this._data.wishlist.items.length !== rawBefore) {
					this._data.wishlist.count = this._data.wishlist.items.length;
					this._markDirty('wishlist');
					this._rebuildIndex('wishlist');
				}
			} else {
				// FIX: Product still exists in another list.
				// Update the raw wishlist item's list-specific fields so the
				// merged counter sends the correct list ID, nonce and token
				// on the next removal instead of repeating the stale values.
				const remainingList = this._data.multiList.lists.find(l => {
					if (String(l.wishlist_id) === String(listId)) return false;
					return (l.wishlist_items || []).some(i => String(i.product_id) === pid);
				});
				if (remainingList) {
					const rawItem = this._data.wishlist.items.find(
						item => String(item.product_id) === pid
					);
					if (rawItem) {
						rawItem.wishlist_id = String(remainingList.wishlist_id ?? remainingList.id ?? '');
						const remainingItem = (remainingList.wishlist_items || []).find(
							i => String(i.product_id) === pid
						);
						if (remainingItem) {
							if (remainingItem.item_id !== undefined) rawItem.item_id = remainingItem.item_id;
							if (remainingItem.remove_nonce) rawItem.remove_nonce = remainingItem.remove_nonce;
							if (remainingItem.wishlist_token) rawItem.wishlist_token = remainingItem.wishlist_token;
							if (remainingItem.remove_url) rawItem.remove_url = remainingItem.remove_url;
						}
						this._markDirty('wishlist');
					}
				}
			}
			this._notify('wishlist', 'multi-list-changed');
		}
		return true;
	}
	/* ═══════════════════════════════════════════
	   REACTIVE SUBSCRIPTIONS (Single Source of Truth)
	════════════════════════════════════════════ */
	subscribe(module, callback) {
		if (!this._listeners.has(module)) {
			this._listeners.set(module, new Set());
		}
		this._listeners.get(module).add(callback);
		return () => {
			this._listeners.get(module)?.delete(callback);
		};
	}
	_notify(module, type, detail = {}) {
		this._scheduleNotify(module);
	}
	_scheduleNotify(module) {
		this._pendingNotifyModules.add(module);
		if (!this._notifyScheduled) {
			this._notifyScheduled = true;
			Promise.resolve().then(() => {
				this._notifyScheduled = false;
				const modules = [...this._pendingNotifyModules];
				this._pendingNotifyModules.clear();
				for (const mod of modules) {
					this._notifyListeners(mod);
				}
			});
		}
	}
	_notifyListeners(module) {
		const listeners = this._listeners.get(module);
		if (!listeners) return;
		for (const cb of listeners) {
			try {
				cb({ module, version: this._version });
			} catch (e) {
				console.warn(`[StateCache] Listener error for ${module}:`, e);
			}
		}
	}
	/* ═══════════════════════════════════════════
	   PERSISTENCE
	════════════════════════════════════════════ */
	_markDirty(module) {
		this._dirty.add(module);
		if (!this._flushTimer) {
			this._flushTimer = setTimeout(() => {
				this._flushTimer = null;
				this._flush();
			}, 200);
		}
	}
	_flush() {
		if (this._dirty.size === 0) return;
		const modulesToFlush = [...this._dirty];
		this._dirty.clear();
		try {
			document.dispatchEvent(new CustomEvent('wlfmc:cache-flush', {
				detail: { modules: modulesToFlush, data: this._data },
			}));
		} catch (e) { /* noop */ }
	}
	getRawForPersistence(modules) {
		const result = {};
		for (const mod of modules) {
			if (this._data[mod]) {
				result[mod] = { ...this._data[mod] };
			}
		}
		return result;
	}
	_rebuildIndex(module) {
		const index = this._index[module];
		if (!index) return;
		index.clear();
		if (module === 'multiList') {
			const lists = this._data.multiList.lists || [];
			for (const list of lists) {
				index.set(String(list.wishlist_id), list);
			}
		} else {
			const items = this._data[module].items || [];
			const keyField = 'product_id';
			for (const item of items) {
				if (item && item[keyField] !== undefined) {
					index.set(String(item[keyField]), item);
				}
			}
		}
	}
	get version() {
		return this._version;
	}
	clear() {
		for (const module of Object.keys(this._data)) {
			if (module === 'config') {
				this._data.config = { userId: '', sessionId: '', isLoggedIn: false, mergeLists: false };
			} else if (module === 'multiList') {
				this._data.multiList = { lists: [], count: 0 };
			} else {
				this._data[module] = { items: [], count: 0 };
			}
		}
		for (const index of Object.values(this._index)) {
			index.clear();
		}
		this._version++;
		this._notify('wishlist', 'clear');
		this._notify('waitlist', 'clear');
		this._notify('multiList', 'clear');
	}
}
export const stateCache = new StateCache();
export default stateCache;
